import { Vertical } from './../models/Vertical'
import { Injectable } from '@angular/core'
import { map, share, tap } from 'rxjs/operators'
import { forkJoin, from } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { ContextService } from './context.service'
import { extent } from 'd3-array'
import { timeWeek, timeDay, timeMonth } from 'd3-time'
import {
  toDate,
  startOfMonth,
  startOfWeek,
  startOfDay,
  diffMonths,
  diffWeeks,
  diffDays,
  addMonths,
  addWeeks,
  addDays,
} from 'app/utils/date'
import { Colors } from '../common/config/constants'
import { hexToRgbA } from 'app/utils/colors'

@Injectable({
  providedIn: 'root',
})
export class HistoricalService {
  currentVerticalId = null
  deliveryNameMap: Record<number, string> = {}
  categoryNameMap: Record<string, string> = {
    nvisitors: 'Visitors',
    nvisits: 'Visits',
    top: 'Top 5',
    bottom: 'Bottom 5',
    true: 'Competitors',
    false: 'Own',
    reference: 'Reference',
    compared: 'Compared',
  }

  chartList = [
    {
      id: 'visitor',
      type: 'bar',
      title: '',
      open: false,
      loading: false,
      targets: null,
      config: {
        barWidth: 50,
        mode: 'grouped',
        colors: [Colors.colorAccent, Colors.colorAccentLight],
        showTotalUnit: false,
        useUnit: false,
        useIsoline: false,
      },
      serviceMethod: this.getTotalVisitors.bind(this),
    },
    {
      id: 'gender',
      type: 'bar',
      title: this.translateService.instant('Gender distribution'),
      open: false,
      loading: false,
      targets: ['gender'],
      config: {
        filterKey: 'gender',
        barWidth: 50,
        mode: 'grouped',
        colors: [Colors.colorMen, Colors.colorWomen],
        colorsCompared: [Colors.colorAccent, Colors.colorSecondary],
        categoriesSort: [],
        xAxisLegendLabel: 'Range by hours',
        xAxisSort: ['Morning', 'Afternoon', 'Evening', 'Night'],
      },
      serviceMethod: this.getGenderDistribution.bind(this),
    },
    {
      id: 'age',
      type: 'bar',
      title: this.translateService.instant('Age range distribution'),
      open: false,
      loading: false,
      targets: ['age'],
      config: {
        filterKey: 'age',
        barWidth: 50,
        mode: 'stacked',
        colors: Colors.barChart,
        colorsCompared: Colors.barChartCompared.slice().reverse(),
        categoriesSort: ['18_24', '25_34', '35_44', '45_54', '55_64', '65_100'],
        xAxisLegendLabel: 'Range by hours',
        xAxisSort: ['Morning', 'Afternoon', 'Evening', 'Night'],
      },
      serviceMethod: this.getAgeRangeDistribution.bind(this),
    },
    {
      id: 'habits',
      type: 'linear',
      title: this.translateService.instant('Habits'),
      open: false,
      loading: false,
      excludeHistorical: true,
      targets: ['habits'],
      config: {
        filterKey: 'weekday',
        barWidth: 1,
        mode: 'linear',
        colors: [Colors.colorMen, Colors.colorWomen],
        colorsCompared: [Colors.colorAccent, Colors.colorSecondary],
        categoriesSort: [],
        xAxisLegendLabel: 'Range by hours',
        xAxisSort: ['Morning', 'Afternoon', 'Evening', 'Night'],
      },
      serviceMethod: this.getGenderDistribution.bind(this),
    },
    {
      id: 'nvisits_distribution',
      type: 'bar',
      title: {
        nvisitors: this.translateService.instant(
          'Distribution of visitors by frequency of visits',
        ),
        nvisits: this.translateService.instant(
          'Visits distribution by visits frequency',
        ),
      },
      open: false,
      loading: false,
      targets: ['nvisits_distribution'],
      onlyVisitsAvailable: true,
      config: {
        filterKey: 'nvisits_distribution',
        barWidth: 50,
        mode: 'stacked',
        colors: Colors.barChart,
      },
      serviceMethod: this.getVisitsVisitorsDistribution.bind(this),
    },
    {
      id: 'avg_visit_duration',
      type: 'bar',
      title: {
        nvisitors: this.translateService.instant(
          'Number of visitors by average visit duration (min)',
        ),
        nvisits: this.translateService.instant(
          "Number of visits by visitor's average visit duration (min)",
        ),
      },
      open: false,
      loading: false,
      targets: ['avg_visit_duration'],
      onlyVisitsAvailable: true,
      config: {
        filterKey: 'avg_visit_duration',
        barWidth: 50,
        mode: 'stacked',
        colors: Colors.barChart,
      },
      serviceMethod: this.getVisitsVisitorsDuration.bind(this),
    },
    {
      id: 'visits_by_duration',
      type: 'line',
      title: this.translateService.instant(
        'Number of visits by duration (min)',
      ),
      onlyVisits: true,
      open: false,
      loading: false,
      targets: null,
      config: {
        excludeMode: false,
        categoryKey: 'category',
        colors: Colors.lineChart,
      },
      serviceMethod: this.getVisitsDuration.bind(this),
    },
    {
      id: 'visitor_profile',
      type: 'bar',
      title: this.translateService.instant('Type of visitors'),
      open: false,
      loading: false,
      targets: ['visitor_profile'],
      longLegend: true,
      config: {
        filterKey: 'visitor_profile',
        barWidth: 50,
        mode: 'stacked',
        colors: Colors.barChart,
      },
      serviceMethod: this.getTypeOfVisitorsDistribution.bind(this),
    },
    {
      id: 'visitor_hours',
      type: 'heatmap',
      title: {
        nvisitors: this.translateService.instant('Visitors per hours'),
        nvisits: this.translateService.instant('Visits per hours'),
      },
      open: false,
      loading: false,
      targets: null,
      config: {
        colors: Colors.heatmapChart,
      },
      serviceMethod: this.getVisitorsPerHours.bind(this),
    },
    {
      id: 'own_competitors',
      type: 'line',
      title: this.translateService.instant('Own vs competitors'),
      open: false,
      loading: false,
      targets: null,
      config: {
        verticals: ['retail'],
        excludeMode: false,
        categoryKey: 'brand',
        colors: Colors.lineChart,
      },
      serviceMethod: this.getVisitorsFromPois(null),
    },
    {
      id: 'store_more_visitors',
      type: 'ranking',
      title: {
        nvisitors: this.translateService.instant('Stores with more visitors'),
        nvisits: this.translateService.instant('Stores with more visits'),
      },
      open: false,
      loading: false,
      targets: null,
      config: {
        verticals: ['retail'],
        excludeMode: true,
        categoryKey: 'competitor',
        categories: [false, true],
        labels: [
          this.translateService.instant('Top 5 own stores'),
          this.translateService.instant('Top 5 competitor stores'),
        ],
        colors: Colors.rankingChart,
      },
      serviceMethod: this.getVisitorsFromPois('top'),
    },
    {
      id: 'store_less_visitors',
      type: 'ranking',
      title: {
        nvisitors: this.translateService.instant('Stores with less visitors'),
        nvisits: this.translateService.instant('Stores with less visits'),
      },
      open: false,
      loading: false,
      targets: null,
      config: {
        verticals: ['retail'],
        excludeMode: true,
        categoryKey: 'competitor',
        categories: [false, true],
        labels: [
          this.translateService.instant('Bottom 5 own stores'),
          this.translateService.instant('Bottom 5 competitor stores'),
        ],
        colors: Colors.rankingChart,
      },
      serviceMethod: this.getVisitorsFromPois('bottom'),
    },
    {
      id: 'visitor_municipalities',
      type: 'ranking',
      title: this.getWidgetTitle('region_local'),
      open: false,
      loading: false,
      targets: null,
      config: {
        excludeMode: true,
        categoryKey: 'ranking',
        categories: ['top', 'bottom'],
        labels: [
          this.translateService.instant('Top 5'),
          this.translateService.instant('Bottom 5'),
        ],
        colors: Colors.rankingChart,
      },
      serviceMethod: this.getVisitorsFromMunicipalities.bind(this),
    },
    {
      id: 'visitor_lsoa',
      type: 'ranking',
      title: this.getWidgetTitle('sub_local_political'),
      open: false,
      loading: false,
      targets: null,
      config: {
        excludeMode: true,
        categoryKey: 'ranking',
        categories: ['top', 'bottom'],
        labels: [
          this.translateService.instant('Top 5'),
          this.translateService.instant('Bottom 5'),
        ],
        colors: Colors.rankingChart,
      },
      serviceMethod: this.getVisitorsFromLSOA.bind(this),
    },
    {
      id: 'visitor_boroughs',
      type: 'ranking',
      title: this.getWidgetTitle('sub_local_alternative'),
      open: false,
      loading: false,
      targets: null,
      config: {
        excludeMode: true,
        categoryKey: 'ranking',
        categories: ['top', 'bottom'],
        labels: [
          this.translateService.instant('Top 5'),
          this.translateService.instant('Bottom 5'),
        ],
        colors: Colors.rankingChart,
      },
      serviceMethod: this.getVisitorsFromBorough.bind(this),
    },
    {
      id: 'visitor_provinces',
      type: 'ranking',
      title: this.getWidgetTitle('region_small'),
      open: false,
      loading: false,
      targets: null,
      config: {
        excludeMode: true,
        categoryKey: 'ranking',
        categories: ['top', 'bottom'],
        labels: [
          this.translateService.instant('Top 5'),
          this.translateService.instant('Bottom 5'),
        ],
        colors: Colors.rankingChart,
      },
      serviceMethod: this.getVisitorsFromProvinces.bind(this),
    },
    {
      id: 'visitor_countries',
      type: 'ranking',
      title: this.getWidgetTitle('country'),
      open: false,
      loading: false,
      targets: null,
      config: {
        excludeMode: true,
        categoryKey: 'ranking',
        categories: ['top', 'bottom'],
        labels: [
          this.translateService.instant('Top 5'),
          this.translateService.instant('Bottom 5'),
        ],
        colors: Colors.rankingChart,
      },
      serviceMethod: this.getVisitorsFromCountries.bind(this),
    },
  ]

  private queryService: any = new Proxy(
    {},
    { get: () => () => Promise.resolve({ rows: [] }) },
  )

  constructor(
    private context: ContextService,
    private translateService: TranslateService,
  ) {
    this.chartList = this.chartList.map((c) => ({
      ...c,
      config: {
        ...c.config,
        useUnit: c.config.useUnit === undefined ? true : c.config.useUnit,
        showTotalUnit:
          c.config.showTotalUnit === undefined ? true : c.config.showTotalUnit,
        categoryNames: this.categoryNameMap,
        deliveryNames: this.deliveryNameMap,
        getDateLimits: this.getDateLimits.bind(this),
        getCurrentGranularity: this.getCurrentGranularity.bind(this),
        getTickInterval: () => 12, // this.getTickInterval.bind(this)
      },
    })) as any[]
  }

  get currentDelivery() {
    return this.context.vertical.delivery
  }

  getTerrunitTypeLabel(terrunit) {
    const region = this.context.organization.terrunit_region_id
    const labels = {}
    return labels && labels[terrunit]
  }

  getWidgetTitle(terrunit) {
    const keyIndicator = this.context.keyIndicator.label
    const keyLabel = this.translateService.instant(`${keyIndicator} from`)
    const terrunitLabel = this.translateService.instant(
      this.getTerrunitTypeLabel(terrunit) || 'NA',
    )
    return `${keyLabel} ${terrunitLabel}`
  }

  isRetail() {
    return this.context.vertical.delivery.type === 'retail'
  }

  reloadChartsData(chartList = this.chartList) {
    for (const c of chartList) {
      this.checkVisibility(c)
      c.serviceMethod(c)
    }
  }

  getCurrentGranularity() {
    const currentDelivery = this.context.vertical.delivery
    return currentDelivery.granularity
  }

  getTickInterval() {
    const currentDelivery = this.context.vertical.delivery
    const granularity = currentDelivery.granularity
    const tickInterval =
      granularity === 'week'
        ? timeWeek.every(1)
        : granularity === 'day'
        ? timeDay.every(1)
        : timeMonth.every(1)
    return tickInterval
  }

  getDateLimits() {
    const currentDelivery = this.context.vertical.delivery
    const isWeekly = currentDelivery.granularity === 'week'
    const isDaily = currentDelivery.granularity === 'day'
    return isWeekly
      ? this.getWeeklyLimits()
      : isDaily
      ? this.getDailyLimits()
      : this.getYearLimits()
  }

  getYearLimits() {
    const sortedDeliveries = [
      ...this.context.vertical.deliveries[this.getTerritory().name],
    ]
      .sort((a, b) => {
        const aAsDate = new Date(a.start)
        const bAsDate = new Date(b.start)
        return aAsDate.getTime() - bAsDate.getTime()
      })
      .slice(-13)

    const start = startOfMonth(toDate(sortedDeliveries[0].start))
    const end = startOfMonth(
      toDate(sortedDeliveries[sortedDeliveries.length - 1].start),
    )
    const monthDiff = diffMonths(end, start)
    const half = addMonths(start, Math.floor(monthDiff / 2))
    return [addMonths(half, -6), addMonths(half, 6)]
  }

  getWeeklyLimits() {
    const deliveries =
      this.context.vertical.deliveries[this.getTerritory().name]
    const dataLimits = extent(deliveries, (d) => Number(new Date(d.start)))
    const start = startOfWeek(new Date(dataLimits[0]))
    const end = startOfWeek(new Date(dataLimits[1]))
    const wDiff = diffWeeks(end, start)
    const half = addWeeks(start, Math.floor(wDiff / 2))
    return [addWeeks(half, -6), addWeeks(half, 6)]
  }

  getDailyLimits() {
    const deliveries =
      this.context.vertical.deliveries[this.getTerritory().name]
    const dataLimits = extent(deliveries, (d) => Number(new Date(d.start)))
    const start = startOfDay(new Date(dataLimits[0]))
    const end = startOfDay(new Date(dataLimits[1]))
    const dDiff = diffDays(end, start)
    const half = addDays(start, Math.floor(dDiff / 2))
    return [addDays(half, -6), addDays(half, 6)]
  }

  checkVisibility(c) {
    const delivery = this.context.vertical.delivery
    const target = delivery.target.subfilters.find((f) => f.value)
    const targetKey = target && target.id
    c.noData = targetKey && c.targets && c.targets.indexOf(targetKey) === -1
    if (c.noData) {
      c.open = false
    }
    if (
      (Array.isArray(c.config.verticals) &&
        c.config.verticals.indexOf(delivery.type) === -1) ||
      (c.onlyVisits && this.context.keyIndicator.value === 'nvisitors') ||
      (c.onlyVisitsAvailable && !this.context.isVisitsAvailable())
    ) {
      c.backupType =
        c.backupType && c.type === 'hidden' ? c.backupType : c.type.slice(0)
      c.type = 'hidden'
    } else if (c.type === 'hidden' && c.backupType) {
      c.type = c.backupType.slice(0)
      delete c.backupType
    }
  }

  updateMaps(vertical: Vertical) {
    if (this.currentVerticalId !== vertical.id) {
      this.currentVerticalId = vertical.id
      this.parseCategoryNames()
      this.parseDeliveryNames()
    }
  }

  parseCategoryNames() {
    const filters = this.context.vertical.delivery.target.subfilters
    filters.forEach((f) => {
      Object.keys(f.values).forEach((key) => {
        this.categoryNameMap[key] = f.values[key].short_label
      })
    })
  }

  parseDeliveryNames() {
    const deliveries =
      this.context.vertical.deliveries[this.getTerritory().name]
    if (!deliveries) return
    deliveries.forEach((d) => {
      this.deliveryNameMap[d.id] = d.name
    })
  }

  private getChartData(c, queryServiceMethod, ...params) {
    if (c.open && JSON.stringify(c.params) !== JSON.stringify(params)) {
      c.loading = true
      c.params = [...params]

      if (c.config.compareOptions) {
        c.data = params[0].map((param) => {
          return from(
            queryServiceMethod.bind(this.queryService)(...[param]),
          ).pipe(
            tap((data: any[]) => {
              try {
                const nodata = data.every(
                  (d) =>
                    !d[this.context.keyIndicator.value] ||
                    d.values.every(
                      (v) =>
                        !v.value &&
                        !v[this.context.keyIndicator.value] &&
                        !v[c.config.keyIndicatorVariable],
                    ),
                )
                c.noData = nodata
                c.open = !nodata
              } catch (e) {
                console.error(
                  '[historical.service] error checking nodata in tap',
                )
              }
              c.loading = false
            }),
            share(),
          )
        })
        c.data = forkJoin(c.data[0], c.data[1]).pipe(
          map((data: any[]) => {
            const res = []
            for (let i = 0; i < data[0].length; i++) {
              const aux = {
                ...data[0][i],
                values: [
                  {
                    category: 'reference',
                    value:
                      data[0][i].value ||
                      data[0][i][
                        c.config.keyIndicatorVariable ||
                          this.context.keyIndicator.value
                      ],
                  },
                  {
                    category: 'compared',
                    value:
                      data[1][i].value ||
                      data[1][i][
                        c.config.keyIndicatorVariable ||
                          this.context.keyIndicator.value
                      ],
                  },
                ],
              }
              delete aux.value
              delete aux[
                c.config.keyIndicatorVariable || this.context.keyIndicator.value
              ]
              res.push(aux)
            }
            return res
          }),
          share(),
        )
      } else {
        c.data = from(Promise.resolve([])).pipe(
          map(
            (data: any[]) => (c.preprocessor && c.preprocessor(data)) || data,
          ),
          tap((data: any[]) => {
            try {
              const nodata = data.every((d) =>
                d.values.every(
                  (v) =>
                    !v.value &&
                    !v[this.context.keyIndicator.value] &&
                    !v[c.config.keyIndicatorVariable],
                ),
              )
              c.noData = nodata
              c.open = !nodata
            } catch (e) {
              console.error('[historical.service] error checking nodata in tap')
            }
            c.loading = false
          }),
          share(),
        )
      }
    }
  }

  private getDestination(poi = null, poiRegion = null) {
    poi = poi || this.context.poi
    poiRegion = poi ? this.context.poiRegion : !poiRegion ? '?' : poiRegion

    const terr = this.getTerritory()
    const mapMode = this.context.mapMode
    const terrunit = mapMode
    return poi
      ? {
          id: poi,
          region_id: poiRegion,
          terrunit,
        }
      : terr
  }

  private getTerritory() {
    return this.context.vertical.delivery.territory
  }

  private getDeliveryId() {
    return this.context.vertical.deliveries[this.getTerritory().name]
  }

  private getVariables(fullStats = true) {
    const target = this.context.vertical.delivery.target.subfilters.find(
      (f) => f.value,
    )
    let value = target && target.value
    if (!value && fullStats) {
      value = this.context.keyIndicatorOptions.map((el) => ({
        [el.value]: [el.value],
      }))

      if (this.context.isVisitsAvailable()) {
        value = value.reduce((bef, el) => ({ ...bef, ...el }))
      } else {
        value = value[0]
      }
    }
    if (Array.isArray(value)) {
      value = value
        .filter((item, idx, arr) => arr.indexOf(item) === idx)
        .slice()
    }
    return value
  }

  private getFilter() {
    return this.context
      .getDeliveryFilters()
      .filter((f) => f.value)
      .map((f) => f.value)
      .join('-')
  }

  private getFilterDst() {
    return this.context
      .getDeliveryFiltersDst()
      .filter((f) => f.value)
      .map((f) => f.value)
      .join('-')
  }

  getTotalVisitors(c) {
    let params
    if (
      c.config.compareOptions &&
      c.config.compareOptions.poisToCompare.length !== 0
    ) {
      params = [
        {
          delivery_id: this.getDeliveryId(),
          variables: this.getVariables(false) || {
            [c.config.keyIndicatorVariable]: [c.config.keyIndicatorVariable],
          },
          filter: this.getFilter(),
          filterDst: this.getFilterDst(),
          dst: this.getDestination(c.poi),
        },
        {
          delivery_id: this.getDeliveryId(),
          variables: this.getVariables(false) || {
            [c.config.keyIndicatorVariable]: [c.config.keyIndicatorVariable],
          },
          filter: this.getFilter(),
          filterDst: this.getFilterDst(),
          dst: {
            ...this.getDestination(c.poi),
            id: c.config.compareOptions.poisToCompare[0],
          },
        },
      ]
      if (c.config.useIsoline) {
        params = params.map((param) => {
          param['isoline'] = this.context.isoline
        })
      }
    } else {
      params = {
        delivery_id: this.getDeliveryId(),
        variables: this.getVariables(),
        filter: this.getFilter(),
        filterDst: this.getFilterDst(),
        dst: this.getDestination(c.poi),
      }
      if (c.config.useIsoline) {
        params['isoline'] = this.context.isoline
      }
      if (typeof params.variables === 'string') {
        const key = params.variables.split('_')[0]
        const variables = {
          [key]: [params.variables],
        }
        if (this.context.isVisitsAvailable()) {
          const extraKey = this.context.keyIndicatorOptions.find(
            (f) => f.value !== key,
          ).value
          variables[extraKey] = [
            (params.variables || params.variables[key]).replace(key, extraKey),
          ]
        }
        params.variables = variables
      } else if (Array.isArray(params.variables)) {
        const key = params.variables[0].split('_')[0]
        const variables = {
          [key]: params.variables,
        }
        if (this.context.isVisitsAvailable()) {
          const extraKey = this.context.keyIndicatorOptions.find(
            (f) => f.value !== key,
          ).value
          variables[extraKey] = (
            Array.isArray(params.variables)
              ? params.variables
              : params.variables[key]
          ).map((item) => item.replace(key, extraKey))
        }
        params.variables = variables
      }
    }
    c.preprocessor = (data: any[]) => {
      return data
        .map((row) => ({
          delivery_id: row['delivery_id'],
          start: row['start'],
          finish: row['finish'],
          values: Object.keys(row)
            .map((key) => {
              if (
                key.startsWith('nvisitors') ||
                key.startsWith('nvisits') ||
                key === 'value'
              ) {
                return {
                  category:
                    key === 'value' ? this.context.keyIndicator.value : key,
                  value: row[key],
                }
              }
            })
            .filter((d) => d), // Remove empty values
        }))
        .slice()
    }
    if (c.title === '') {
      c.title = this.context.isVisitsAvailable()
        ? this.translateService.instant('Total visitors and visits')
        : this.translateService.instant('Total visitors')
      c.config.showTotalUnit = !this.context.isVisitsAvailable()
    }
    this.getChartData(c, () => Promise.resolve([]), params)
  }

  private getVariablesByTargetVariables(variables) {
    return variables && variables.value
      ? Array.isArray(variables.value)
        ? [...variables.value]
        : [variables.value]
      : [...Object.keys(variables.values)]
  }

  getGenderDistribution(c) {
    const target = this.context.vertical.delivery.target
    const variables =
      target && target.subfilters.find((f) => f.id === c.config.filterKey)
    if (!variables) {
      c.open = false
      c.noData = true
      return
    }
    const params = {
      delivery_id: this.getDeliveryId(),
      variables: this.getVariablesByTargetVariables(variables),
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      dst: this.getDestination(c.poi),
    }
    if (c.config.useIsoline) {
      params['isoline'] = this.context.isoline
    }
    this.getChartData(c, () => Promise.resolve([]), 'gender', params, false)
  }

  getTypeOfVisitorsDistribution(c) {
    const target = this.context.vertical.delivery.target
    const variables =
      target && target.subfilters.find((f) => f.id === c.config.filterKey)
    if (!variables) {
      c.open = false
      c.noData = true
      return
    }
    const params = {
      delivery_id: this.getDeliveryId(),
      variables: this.getVariablesByTargetVariables(variables),
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      dst: this.getDestination(c.poi),
    }
    if (c.config.useIsoline) {
      params['isoline'] = this.context.isoline
    }
    this.getChartData(c, this.queryService.getVisitorsBy, 'type', params, false)
  }

  getAgeRangeDistribution(c) {
    const target = this.context.vertical.delivery.target
    const variables =
      target && target.subfilters.find((f) => f.id === c.config.filterKey)
    if (!variables) {
      c.open = false
      c.noData = true
      return
    }
    const params = {
      delivery_id: this.getDeliveryId(),
      variables: this.getVariablesByTargetVariables(variables),
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      dst: this.getDestination(c.poi),
    }
    if (c.config.useIsoline) {
      params['isoline'] = this.context.isoline
    }
    this.getChartData(c, this.queryService.getVisitorsBy, 'age', params, false)
  }

  getVisitsVisitorsDistribution(c) {
    const target = this.context.vertical.delivery.target
    const variables =
      target && target.subfilters.find((f) => f.id === c.config.filterKey)
    if (!variables) {
      c.open = false
      c.noData = true
      return
    }
    const params = {
      delivery_id: this.getDeliveryId(),
      variables: this.getVariablesByTargetVariables(variables),
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      dst: this.getDestination(c.poi),
    }
    if (c.config.useIsoline) {
      params['isoline'] = this.context.isoline
    }
    this.getChartData(
      c,
      () => Promise.resolve([]),
      'nvisits_distribution',
      params,
      false,
    )
  }

  getVisitsVisitorsDuration(c) {
    const target = this.context.vertical.delivery.target
    const variables =
      target && target.subfilters.find((f) => f.id === c.config.filterKey)
    if (!variables) {
      c.open = false
      c.noData = true
      return
    }
    const params = {
      delivery_id: this.getDeliveryId(),
      variables: this.getVariablesByTargetVariables(variables),
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      dst: this.getDestination(c.poi),
    }
    if (c.config.useIsoline) {
      params['isoline'] = this.context.isoline
    }
    this.getChartData(
      c,
      () => Promise.resolve([]),
      'avg_visit_duration',
      params,
      false,
    )
  }

  getVisitsDuration(c) {
    const target = this.context.vertical.delivery.target
    const variables =
      target && target.subfilters.find((f) => f.id === 'duration')
    if (!variables) {
      c.open = false
      c.noData = true
      return
    }
    const params = {
      delivery_id: this.getDeliveryId(),
      variables: this.getVariablesByTargetVariables(variables),
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      dst: this.getDestination(c.poi),
    }
    if (c.config.useIsoline) {
      params['isoline'] = this.context.isoline
    }
    this.getChartData(c, () => Promise.resolve([]), 'duration', params, false)
  }

  getVisitorsPerHours(c) {
    const params = {
      delivery_id: this.getDeliveryId(),
      variables: this.getVariables(false) || [this.context.keyIndicator.value],
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      dst: this.getDestination(c.poi),
    }
    if (c.config.useIsoline) {
      params['isoline'] = this.context.isoline
    }
    const fnWrapper = (..._params) => {
      return this.queryService.getTimeHeatmap(..._params).then((data) => {
        const hourFilter = this.context
          .getDeliveryFilters()
          .find((f) => f.id === 'hour')
        if (!data || !data.length || !hourFilter) {
          return data
        }
        const info = hourFilter.values
        return data.map((d) => {
          d.values.sort((a, b) => {
            const o1 = info[a.interval].order
            const o2 = info[b.interval].order
            return o1 - o2
          })
          return d
        })
      })
    }
    this.getChartData(c, () => Promise.resolve([]), params)
  }

  getVisitorsFromPois(ranking) {
    return (c) => {
      const params = {
        delivery_id: this.getDeliveryId(),
        variables: this.getVariables(false) || [
          this.context.keyIndicator.value,
        ],
        filter: this.getFilter(),
        filterDst: this.getFilterDst(),
        dst: this.isRetail() ? null : this.getDestination(c.poi),
      }
      if (c.config.useIsoline) {
        params['isoline'] = this.context.isoline
      }
      const fnWrapper = (..._params) => {
        return this.queryService.getRanking(..._params).then((data) =>
          data.map((d) => {
            if (ranking) {
              d.values = d.values.filter((v) => v.ranking === ranking)
            } else {
              const groups = d.values.reduce((acum, elem) => {
                elem.name = elem[c.config.categoryKey]
                elem.name = elem.name[0].toUpperCase() + elem.name.substring(1)
                if (!acum[elem.brand]) {
                  acum[elem.brand] = elem
                  return acum
                }
                acum[elem.brand].value += elem.value
                return acum
              }, {})
              d.values = Object.values(groups)
            }
            return d
          }),
        )
      }
      this.getChartData(c, () => Promise.resolve([]), params)
    }
  }

  getVisitorsFromMunicipalities(c) {
    const currentDelivery = this.context.vertical.delivery
    const params = {
      ref_delivery_id: currentDelivery.id,
      delivery_id: this.getDeliveryId(),
      variables: this.getVariables(false) || [this.context.keyIndicator.value],
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      // dst: this.isRetail() ? null : this.getDestination(c.poi)
      dst: this.getDestination(c.poi),
    }
    c.title = this.getWidgetTitle('region_local')
    this.getChartData(c, () => Promise.resolve([]), params)
  }

  getVisitorsFromBorough(c) {
    const currentDelivery = this.context.vertical.delivery
    const params = {
      ref_delivery_id: currentDelivery.id,
      delivery_id: this.getDeliveryId(),
      variables: this.getVariables(false) || [this.context.keyIndicator.value],
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      // dst: this.isRetail() ? null : this.getDestination(c.poi)
      dst: this.getDestination(c.poi),
    }
    c.title = this.getWidgetTitle('sub_local_alternative')
    this.getChartData(c, () => Promise.resolve([]), params)
  }

  getVisitorsFromProvinces(c) {
    const currentDelivery = this.context.vertical.delivery
    const params = {
      ref_delivery_id: currentDelivery.id,
      delivery_id: this.getDeliveryId(),
      variables: this.getVariables(false) || [this.context.keyIndicator.value],
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      // dst: this.isRetail() ? null : this.getDestination(c.poi)
      dst: this.getDestination(c.poi),
    }
    c.title = this.getWidgetTitle('region_small')
    this.getChartData(c, () => Promise.resolve([]), params)
  }

  getVisitorsFromCountries(c) {
    const currentDelivery = this.context.vertical.delivery
    const params = {
      ref_delivery_id: currentDelivery.id,
      delivery_id: this.getDeliveryId(),
      variables: this.getVariables(false) || [this.context.keyIndicator.value],
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      // dst: this.isRetail() ? null : this.getDestination(c.poi)
      dst: this.getDestination(c.poi),
    }
    c.title = this.getWidgetTitle('country')
    this.getChartData(c, () => Promise.resolve([]), params)
  }

  getVisitorsFromLSOA(c) {
    const currentDelivery = this.context.vertical.delivery
    const params = {
      ref_delivery_id: currentDelivery.id,
      delivery_id: this.getDeliveryId(),
      variables: this.getVariables(false) || [this.context.keyIndicator.value],
      filter: this.getFilter(),
      filterDst: this.getFilterDst(),
      // dst: this.isRetail() ? null : this.getDestination(c.poi)
      dst: this.getDestination(c.poi),
    }
    c.title = this.getWidgetTitle('sub_local_political')
    this.getChartData(c, () => Promise.resolve([]), params)
  }
}
